import React, { useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import Checkbox from "./Checkbox";
import Accordion from "./Accordion";
import "./CategoryFilter.css";

const GET_CATEGORIES = gql`
  query getCategoriesCached {
    getCategoriesCached(limit: 100) {
      name
      subcategories {
        id
        name
      }
    }
  }
`;

/**
 * 카테고리 필터 컴포넌트
 * @param categoriesFilter
 * @param subcategoryIds
 * @param onChange
 * @returns {Element}
 * @constructor
 */
const CategoryFilter = ({ categoriesFilter, subcategoryIds, onChange }) => {
  const [fetchCategories, { data }] = useLazyQuery(GET_CATEGORIES);

  const allCategories = useMemo(() => {
    if (data && data.getCategoriesCached) return data.getCategoriesCached;
    return [];
  }, [data]);

  // initial fetch / when gender changes
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSubCategoryChange = (subCategory) => {
    // 타입 일치를 위해 숫자로 변환하여 비교
    const subCategoryId = parseInt(subCategory.id, 10);
    const normalizedSubcategoryIds = subcategoryIds.map((id) =>
      parseInt(id, 10),
    );

    const newIds = normalizedSubcategoryIds.includes(subCategoryId)
      ? normalizedSubcategoryIds.filter((id) => id !== subCategoryId)
      : [...normalizedSubcategoryIds, subCategoryId];
    // remove category names selection when specific sub selected
    const newCatFilters = categoriesFilter.filter(
      (c) => c !== subCategory.name && c !== subCategory.parentName,
    );
    onChange({ categories: newCatFilters, subcategoryIds: newIds });
  };

  const handleSelectAllCategory = (category) => {
    const categorySubIds = category.subcategories.map((sub) =>
      parseInt(sub.id, 10),
    );
    const normalizedSubcategoryIds = subcategoryIds.map((id) =>
      parseInt(id, 10),
    );

    // 현재 카테고리의 모든 서브카테고리가 선택되어 있는지 확인
    const allSelected = categorySubIds.every((id) =>
      normalizedSubcategoryIds.includes(id),
    );

    let newIds;
    if (allSelected) {
      // 모두 선택되어 있으면 해제
      newIds = normalizedSubcategoryIds.filter(
        (id) => !categorySubIds.includes(id),
      );
    } else {
      // 일부만 선택되어 있거나 아무것도 선택되지 않았으면 모두 선택
      const idsToAdd = categorySubIds.filter(
        (id) => !normalizedSubcategoryIds.includes(id),
      );
      newIds = [...normalizedSubcategoryIds, ...idsToAdd];
    }

    // remove category names selection when specific sub selected
    const newCatFilters = categoriesFilter.filter((c) => c !== category.name);
    onChange({ categories: newCatFilters, subcategoryIds: newIds });
  };

  const isCategoryAllSelected = (category) => {
    const categorySubIds = category.subcategories.map((sub) =>
      parseInt(sub.id, 10),
    );
    const normalizedSubcategoryIds = subcategoryIds.map((id) =>
      parseInt(id, 10),
    );
    return categorySubIds.every((id) => normalizedSubcategoryIds.includes(id));
  };

  const titleStyle = {
    fontSize: "0.9375rem", // 15px
    fontStyle: "normal",
    fontWeight: 350,
    lineHeight: "1.25rem", // 20px
    letterSpacing: "-0.025rem", // -0.4px
  };

  return (
    <div className="category-filter-container">
      {allCategories.map((category) => (
        <Accordion
          key={category.name}
          title={category.name}
          titleStyle={titleStyle}
          initialOpen={false}
          type="sub"
        >
          <div className="subcategory-list">
            <Checkbox
              id={`all-${category.name}`}
              key={`all-${category.name}`}
              text="전체"
              checked={isCategoryAllSelected(category)}
              onChange={() => handleSelectAllCategory(category)}
            />
            {category.subcategories.map((sub) => (
              <Checkbox
                id={sub.id}
                key={sub.id}
                text={sub.name}
                checked={subcategoryIds
                  .map((id) => parseInt(id, 10))
                  .includes(parseInt(sub.id, 10))}
                onChange={() =>
                  handleSubCategoryChange({ ...sub, parentName: category.name })
                }
              />
            ))}
          </div>
        </Accordion>
      ))}
    </div>
  );
};

CategoryFilter.propTypes = {
  gender: PropTypes.string,
  categoriesFilter: PropTypes.array.isRequired,
  subcategoryIds: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CategoryFilter;
